import { CheckCircleBroken, ClipboardCheck, File06, FileCheck02, LayersThree02, MarkerPin01, NavigationPointer01 } from '@untitled-ui/icons-react/build/cjs';
import { TFunction } from 'i18next';

type BookingStepKeyType = 'shipper_details' | 'consignee_details' | 'load_details' | 'order_details' | 'documents' | 'bol_requirements' | 'review_confirm' | 'stop';

export interface IBookingStep {
    id: number;
    key: BookingStepKeyType;
    title: string;
    icon: React.ElementType;
    screenTitle: string;
    screenDescription: string;
    // Only set for key === 'stop' — which middle stop (quote_stops.position) this step displays.
    // Every 'stop' step shares the same key, so callers must key React lists off `id`, not `key`.
    position?: number;
}

// The slice of a middle stop this module needs to label its sidebar tab. Matches the stop shape
// already held in bookingFormData.stops (see BookingStopDetails' IBookingStop).
export interface IBookingStopSummary {
    position: number;
    city?: string | null;
    state?: string | null;
    stateCode?: string | null;
}

// Sidebar tab label for a middle stop. Plain "Stop N" until the stop has a city, then
// "Stop N · City, ST" — same convention as the Add Quote stepper (see addQuote.const.ts).
// The state code is dropped rather than rendered blank when it is missing, so the label never
// ends in a dangling comma.
const buildStopTitle = (t: TFunction, stop: IBookingStopSummary): string => {
    const location = [stop.city, stop.stateCode || stop.state].filter(Boolean).join(', ');

    if (!stop.city) {
        return t('BookOrder.Booking_Stepper.Stop.title', { position: stop.position });
    }

    return t('BookOrder.Booking_Stepper.Stop.title_with_location', { position: stop.position, location });
};

export const BOOKING_KEYS: Record<string, BookingStepKeyType> = {
    SHIPPER_DETAILS: 'shipper_details',
    CONSIGNEE_DETAILS: 'consignee_details',
    LOAD_DETAILS: 'load_details',
    ORDER_DETAILS: 'order_details',
    DOCUMENTS: 'documents',
    BOL_REQUIREMENTS: 'bol_requirements',
    REVIEW_CONFIRM: 'review_confirm',
};

export const BOOKING_STEPS = (t: TFunction): IBookingStep[] => [
    {
        id: 1,
        key: BOOKING_KEYS.SHIPPER_DETAILS,
        title: t('BookOrder.Booking_Stepper.Shipper_Details.title'),
        icon: MarkerPin01,
        screenTitle: t('BookOrder.Booking_Stepper.Shipper_Details.title'),
        screenDescription: t('BookOrder.Booking_Stepper.Shipper_Details.description'),
    },
    {
        id: 2,
        key: BOOKING_KEYS.CONSIGNEE_DETAILS,
        title: t('BookOrder.Booking_Stepper.Consignee_Details.title'),
        icon: NavigationPointer01,
        screenTitle: t('BookOrder.Booking_Stepper.Consignee_Details.title'),
        screenDescription: t('BookOrder.Booking_Stepper.Consignee_Details.description'),
    },
    {
        id: 3,
        key: BOOKING_KEYS.ORDER_DETAILS,
        title: t('BookOrder.Booking_Stepper.Order_Details.title'),
        icon: ClipboardCheck,
        screenTitle: t('BookOrder.Booking_Stepper.Order_Details.title'),
        screenDescription: t('BookOrder.Booking_Stepper.Order_Details.description'),
    },
    {
        id: 4,
        key: BOOKING_KEYS.LOAD_DETAILS,
        title: t('BookOrder.Booking_Stepper.Load_Details.title'),
        icon: LayersThree02,
        screenTitle: t('BookOrder.Booking_Stepper.Load_Details.title'),
        screenDescription: t('BookOrder.Booking_Stepper.Load_Details.description'),
    },
    {
        id: 5,
        key: BOOKING_KEYS.DOCUMENTS,
        title: t('BookOrder.Booking_Stepper.Documents.title'),
        icon: File06,
        screenTitle: t('BookOrder.Booking_Stepper.Documents.title'),
        screenDescription: t('BookOrder.Booking_Stepper.Documents.description'),
    },
    {
        id: 6,
        key: BOOKING_KEYS.BOL_REQUIREMENTS,
        title: t('BookOrder.Booking_Stepper.Bol_Requirements.title'),
        icon: FileCheck02,
        screenTitle: t('BookOrder.Booking_Stepper.Bol_Requirements.screen_title'),
        screenDescription: t('BookOrder.Booking_Stepper.Bol_Requirements.screen_description'),
    },
    {
        id: 7,
        key: BOOKING_KEYS.REVIEW_CONFIRM,
        title: t('BookOrder.Booking_Stepper.Review_Confirm.title'),
        icon: CheckCircleBroken,
        screenTitle: t('BookOrder.Booking_Stepper.Review_Confirm.title'),
        screenDescription: t('BookOrder.Booking_Stepper.Review_Confirm.description'),
    },
];

// Client decision — read-only "Stop N" steps (see BookingStopDetails.tsx), one per middle stop
// (quote_stops position 2..N-1), inserted between Shipper Details and Consignee Details — mirrors
// the Edit Quote modal's own stop-tab ordering. `stops` is expected pre-sorted ascending by
// position (the physical route order); an empty array (classic or WAL-created quotes with no
// customer-requested middle stops) leaves the step list unchanged from before this feature.
// The screen heading stays the plain "Stop N" — only the sidebar tab carries the city, because the
// screen already shows the full address right below it.
export const getFilteredBookingSteps = (t: TFunction, isCrossBorder: boolean, stops: IBookingStopSummary[] = []): IBookingStep[] => {
    const base = BOOKING_STEPS(t).filter((step) => (step.key === BOOKING_KEYS.DOCUMENTS ? isCrossBorder : true));
    const shipperIndex = base.findIndex((step) => step.key === BOOKING_KEYS.SHIPPER_DETAILS);

    const stopSteps: IBookingStep[] = stops.map((stop) => ({
        id: 0, // reassigned below, alongside every other step
        key: 'stop',
        title: buildStopTitle(t, stop),
        icon: MarkerPin01,
        screenTitle: t('BookOrder.Booking_Stepper.Stop.title', { position: stop.position }),
        screenDescription: t('BookOrder.Booking_Stepper.Stop.description'),
        position: stop.position,
    }));

    const withStops = shipperIndex === -1 ? base : [...base.slice(0, shipperIndex + 1), ...stopSteps, ...base.slice(shipperIndex + 1)];

    return withStops.map((step, index) => ({ ...step, id: index + 1 }));
};
